import React from 'react';
import { Home, Trophy, Swords, User, Users, LayoutGrid } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { getAppNotificationSummary } from '../services/database';
import { playSound } from '../services/audio';
import BrandMark from './BrandMark';

// Direct re-exports to ensure modules are loaded correctly
export { Link, useLocation, useNavigate, useParams };

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [appNotificationCount, setAppNotificationCount] = React.useState(0);
  const [currentUserId, setCurrentUserId] = React.useState<string | null>(null);

  const isActive = (path: string) => location.pathname.startsWith(path);

  React.useEffect(() => {
    const bootstrapLayoutSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUserId(session?.user?.id || null);
    };

    void bootstrapLayoutSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUserId(session?.user?.id || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  React.useEffect(() => {
    const loadHeaderNotifications = async () => {
      if (!currentUserId) {
        setAppNotificationCount(0);
        return;
      }

      const appSummary = await getAppNotificationSummary(currentUserId);
      setAppNotificationCount(appSummary.total);
    };

    void loadHeaderNotifications();

    let activeChannel: ReturnType<typeof supabase.channel> | null = null;

    if (currentUserId) {
      activeChannel = supabase
        .channel(`layout-notifications:${currentUserId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'direct_conversations' }, () => {
          void loadHeaderNotifications();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'friend_requests' }, () => {
          void loadHeaderNotifications();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'friend_duels' }, () => {
          void loadHeaderNotifications();
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'app_notifications' }, () => {
          void loadHeaderNotifications();
        })
        .subscribe();
    };

    return () => {
      if (activeChannel) {
        supabase.removeChannel(activeChannel);
      }
    };
  }, [currentUserId]);

  const NavItem = ({ to, icon: Icon, label, badgeCount = 0 }: { to: string; icon: any; label: string; badgeCount?: number }) => (
    <Link
      to={to}
      onClick={() => {
        void playSound('tap');
      }}
      className={`flex flex-col items-center justify-center min-w-0 w-full h-full rounded-2xl px-2 py-2 space-y-1 transition-all ${
        isActive(to)
          ? 'text-neon-purple bg-neon-purple/10 shadow-[0_8px_24px_rgba(168,85,247,0.16)]'
          : 'text-gray-500 hover:text-gray-300'
      }`}
    >
      <div className="relative">
        <Icon size={22} strokeWidth={isActive(to) ? 2.5 : 2} />
        {badgeCount > 0 && (
          <span className="absolute -right-2 -top-2 min-w-[18px] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-white">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </div>
      <span className="text-[9px] uppercase font-bold tracking-wider truncate">{label}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-background text-white font-sans selection:bg-neon-purple selection:text-white pb-24 md:pb-0 md:pl-20">
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-full w-20 flex-col items-center border-r border-gray-800 bg-card z-50 py-8">
        <div className="mb-8">
            <BrandMark imageClassName="h-10 w-10 rounded-2xl" showTitle={false} />
        </div>
        <nav className="flex flex-col space-y-8 w-full">
            <NavItem to="/app/home" icon={Home} label="Base" />
            <NavItem to="/app/challenges" icon={Swords} label="Play" />
            <NavItem to="/app/nexus" icon={LayoutGrid} label="Nexus" />
            <NavItem to="/app/friends" icon={Users} label="Squad" badgeCount={appNotificationCount} />
            <NavItem to="/app/ranking" icon={Trophy} label="Rank" />
            <NavItem to="/app/profile" icon={User} label="Perfil" />
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="w-full max-w-lg mx-auto md:max-w-2xl lg:max-w-4xl min-h-screen px-4 pt-4 pb-6 md:p-8">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex h-20 max-w-lg items-center justify-between gap-1 rounded-[28px] border border-gray-800 bg-card/95 px-2 shadow-[0_-12px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl">
          <NavItem to="/app/home" icon={Home} label="Base" />
          <NavItem to="/app/challenges" icon={Swords} label="Play" />
          <NavItem to="/app/nexus" icon={LayoutGrid} label="Nexus" />
          <NavItem to="/app/friends" icon={Users} label="Squad" badgeCount={appNotificationCount} />
          <NavItem to="/app/profile" icon={User} label="Perfil" />
        </div>
      </nav>
    </div>
  );
};

export default Layout;
